import React, {useCallback, useEffect, useMemo, useReducer, useState} from 'react';
import './app.scss';
import {MultiInput} from "./multiInput.tsx";
import Header from "./header.tsx";

type Error = {
  anagramIndex: number,

  anagram: string,

  missing: string[],
  overflow: string[],
}

const DEFAULT_IGNORE: string = " ,.;!?:()[]{}0123456789&$=%+"
const DEFAULT_INCLUDE: string[][] = [
  ["e", "è", "é", "ë", "ê"],
  ["a", "à", "á", "â", "ä"],
  ["i", "ì", "í"],
  ["o", "ò", "ó", "ô", "ö"],
  ["u", "ù", "ú", "ü"],
  ["c", "ç"],
  ["n", "ñ"],
]

function reduceWithCount(array: string[]): { [key: string]: { count: number } } {
  return array
    // @ts-ignore
    .reduce((acc, curr) => {

      if (acc[curr]) {
        acc[curr].count += 1
      } else {
        acc[curr] = {count: 1}
      }

      return acc
    }, {});
}

type Options = {
  showAmountMissing: boolean,
}

const defaultOptions: Options = {
  showAmountMissing: false,
}

function App() {
  const [title, setTitle] = useState<string>('');
  const [input, setInput] = useState<string>('');
  const [errors, setErrors] = useState<{ [index: number]: Error }>({});

  const [ignore, setIgnore] = useState<string>(DEFAULT_IGNORE);
  const [include, setInclude] = useState<string[][]>(DEFAULT_INCLUDE);
  const [options, setOptions] = useState<Options>(defaultOptions);

  useEffect(() => {
    const newErrors = {}

    const inputSplit = input.split("\n");

    const sortedTitle = title.toLowerCase()
      .split("")
      .filter(x => !ignore.includes(x.trim()))
      .sort((a, b) => a.localeCompare(b));

    inputSplit.forEach((input: string, i: number) => {
      if (input === "") return

      const newAnagramText = input.toLowerCase()
        .split("")
        .filter(x => !ignore.includes(x.trim()))
        .sort((a, b) => a.localeCompare(b));

      const missingInAnagram = [...sortedTitle];
      const overflowInAnagram = [...newAnagramText];

      for (let char of newAnagramText) {
        const includeFound = include.find(i => i.includes(char));
        const indexes = (includeFound || [char]).map(c => missingInAnagram.indexOf(c))
        const everyNotFound = indexes.every(i => i === -1)

        if (everyNotFound) continue;

        missingInAnagram.splice(indexes.find(i => i != -1), 1);
      }

      for (let char of sortedTitle) {
        const includeFound = include.find(i => i.includes(char));
        const indexes = (includeFound || [char]).map(c => overflowInAnagram.indexOf(c))
        const everyNotFound = indexes.every(i => i === -1)

        if (everyNotFound) continue;

        overflowInAnagram.splice(indexes.find(i => i != -1), 1);
      }

      const innerError: Error = {
        anagramIndex: i,
        overflow: overflowInAnagram,
        missing: missingInAnagram,
        anagram: input,
      }

      if (innerError.overflow.length > 0 || innerError.missing.length > 0) {
        newErrors[innerError.anagramIndex] = innerError;
      }
    })

    setErrors(newErrors);
  }, [input, title, ignore, include])

  const transformedInput = useMemo(
    () => {
      const transformedInput = input.toString()
        .split("\n")
        .map((anagram, anagramIndex) => {
          return anagram.split("")
            .map((x, charIndex) =>
                <span
                  key={`${anagramIndex}-${charIndex}`}
                  className={errors[anagramIndex]?.overflow.includes(x) ? "error-span" : ""}>
            {x}
          </span>
            )
        })

      transformedInput.forEach((anagram, anagramIndex) => {
        if (errors[anagramIndex]?.missing.length > 0) {
          const shortened = reduceWithCount(errors[anagramIndex].missing)
          const shortenedText = Object.keys(shortened).map((s, i) => <>
            {options.showAmountMissing && <small key={`${anagramIndex}-${i}-missing`}>{shortened[s].count}</small>} {s},
          </>)

          anagram.push(<span className={"missing-text"} key={`${anagramIndex}-missing`}>Fehlend: {shortenedText}</span>)
        }

        if (errors[anagramIndex]?.overflow.length > 0) {
          const shortened = reduceWithCount(errors[anagramIndex].overflow)
          const shortenedText = Object.keys(shortened).map((s, i) => <>
            {options.showAmountMissing && <small key={`${anagramIndex}-${i}-overflow`}>{shortened[s].count}</small>} {s},
          </>)

          anagram.push(<span className={"overflow-text"} key={`${anagramIndex}-overflow`}>Überfluss: {shortenedText}</span>)
        }

        anagram.push(<br key={`${anagramIndex}-br`}/>)
      })

      return transformedInput
    },
    [input, errors, ignore, include]
  )

  return (
    <div className="app">
      <Header/>

      <main className="app-main">
        <div id={"create-section"} className="section">
          <div className={"inputs-container"}>
            <h2>Erstellen & Prüfen</h2>
            <div className={"input-container"}>
              <label htmlFor={"title"}>Programma</label>
              <input
                id={"title"}
                placeholder={"conversation"}
                className={"app-input"} value={title}
                onChange={(e) => setTitle(e.target.value)}
                onPaste={(e) => {
                  e.preventDefault();

                  // Get the title of the anagram
                  const data = e.clipboardData.getData("text/plain");
                  const split = data.split("\n").reverse()

                  setTitle(split.pop());
                  setInput(split.reverse().join("\n"));
                }}
              />
            </div>

            <div className={"input-container"}>
              <label htmlFor={"text"}>Texte</label>
              <textarea
                id={"text"}
                autoCapitalize={"none"}
                placeholder={"voice rants on"}
                rows={10}
                autoCorrect={"off"}
                spellCheck={false}
                className="app-input"
                value={input}
                contentEditable={"true"}
                onChange={(e) => setInput(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div id={"b-divider"} className="divider"/>

        <div id={"config-section"} className="section">
          <div className="configuration">
            <h2>Konfiguration</h2>
            <div className="input-container">
              <label htmlFor="ignoreChars">Ignorieren</label>
              <input
                id={"ignoreChars"}
                className="app-input"
                value={ignore}
                onChange={(e) => setIgnore(e.target.value)}
              />
            </div>

            <MultiInput
              label={"Integrieren"}
              values={include.map(strs => strs.join(""))}
              setValues={v => setInclude(v.map(str => str.split("")))}
            />
          </div>
        </div>

        <div id={"representation-section"} className={"section"}>
          <div className="text-representation">
            <h3>{title}</h3>
            {transformedInput}
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;
